import { useTranslation } from 'react-i18next';
import type { OnboardingData, Industry } from './types.js';

interface Props {
  data: OnboardingData;
  onChange: (patch: Partial<OnboardingData>) => void;
  onIndustryChange: (industry: Industry) => void;
  onBack: () => void;
  onNext: () => void;
}

const INDUSTRIES: { value: Industry; key: string }[] = [
  { value: 'print_signage', key: 'industries.print_signage' },
  { value: 'badges_credentials', key: 'industries.badges_credentials' },
  { value: 'apparel_promotional', key: 'industries.apparel_promotional' },
  { value: 'food_beverage', key: 'industries.food_beverage' },
  { value: 'furniture_manufacturing', key: 'industries.furniture_manufacturing' },
  { value: 'software_services', key: 'industries.software_services' },
  { value: 'other', key: 'industries.other' },
];

export default function StepBasics({ data, onChange, onIndustryChange, onBack, onNext }: Props) {
  const { t } = useTranslation('onboarding');
  const canContinue = data.companyName.trim().length > 0;

  return (
    <div className="page-enter">
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--weight-light)',
          color: 'var(--color-text)',
          marginBottom: 'var(--space-8)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        {t('basics.title')}
      </h2>

      {/* Company name */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <label
          htmlFor="company-name"
          style={{
            display: 'block',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-text)',
            marginBottom: 'var(--space-2)',
          }}
        >
          {t('basics.company_name_label')} *
        </label>
        <input
          id="company-name"
          type="text"
          value={data.companyName}
          onChange={(e) => onChange({ companyName: e.target.value })}
          placeholder={t('basics.company_name_placeholder')}
          autoFocus
          style={{
            width: '100%',
            padding: 'var(--space-3) var(--space-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-text)',
            background: 'var(--color-bg)',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Industry */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-text)',
            marginBottom: 'var(--space-3)',
          }}
        >
          {t('basics.industry_label')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {INDUSTRIES.map(({ value, key }) => (
            <label
              key={value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                border: data.industry === value
                  ? '1.5px solid var(--color-brand)'
                  : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                background: data.industry === value ? 'var(--color-brand-pale)' : 'var(--color-bg)',
                transition: 'border-color var(--duration-snap) var(--ease-out)',
              }}
            >
              <input
                type="radio"
                name="industry"
                value={value}
                checked={data.industry === value}
                onChange={() => onIndustryChange(value)}
                style={{ accentColor: 'var(--color-brand)' }}
              />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                {t(key)}
              </span>
            </label>
          ))}
        </div>
        <p
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          {t('basics.industry_hint')}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button
          onClick={onBack}
          style={{
            padding: 'var(--space-3) var(--space-5)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-secondary)',
            background: 'var(--color-bg)',
            cursor: 'pointer',
          }}
        >
          ← {t('actions.back', { ns: 'common' })}
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-5)',
            background: canContinue ? 'var(--color-brand)' : 'var(--color-gray-200)',
            color: canContinue ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: canContinue ? 'pointer' : 'not-allowed',
          }}
        >
          {t('actions.next', { ns: 'common' })} →
        </button>
      </div>
    </div>
  );
}
